import { useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Location, LangCode } from "../data/types.js";
import { t } from "../i18n/strings.js";

interface Props {
  open: boolean;
  locations: Location[];
  lang: LangCode;
  onClose: () => void;
  onPickLocation: (loc: Location) => void;
}

/**
 * Fullscreen vertical-swipe photo feed — "inspiration mode".
 *
 * Each slide is one photo (best shots per pin) with the pin number,
 * name and a CTA that closes the gallery and opens the matching
 * LocationSheet on the underlying map. Pure CSS scroll-snap keeps
 * the swipe gesture native and smooth (no drag library, no jank on
 * low-end mobile).
 */
export function InspirationGallery({
  open,
  locations,
  lang,
  onClose,
  onPickLocation,
}: Props) {
  // Build an interleaved + shuffled feed: layer 0 = first photo of each
  // pin, layer 1 = second photo. Each layer is Fisher–Yates shuffled, then
  // concatenated so the user sees every pin once before any repeats, in a
  // different order each time the gallery opens.
  const slides = useMemo(() => {
    if (!open) return [];
    const layers: Array<Array<{ loc: Location; url: string; key: string }>> = [
      [],
      [],
    ];
    for (const loc of locations) {
      if (loc.isShop) continue;
      const photos = loc.photos ?? [];
      for (let i = 0; i < Math.min(2, photos.length); i++) {
        layers[i].push({
          loc,
          url: photos[i].url,
          key: `${loc.id}-${i}`,
        });
      }
    }
    for (const layer of layers) {
      for (let i = layer.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [layer[i], layer[j]] = [layer[j], layer[i]];
      }
    }
    return [...layers[0], ...layers[1]];
  }, [locations, open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[80] bg-black"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close inspiration"
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform"
            style={{ top: "max(env(safe-area-inset-top, 0px), 16px)" }}
          >
            ✕
          </button>

          {/* Vertical swipe feed */}
          <div
            className="h-full overflow-y-auto"
            style={{
              scrollSnapType: "y mandatory",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {slides.length === 0 ? (
              <div className="h-full flex items-center justify-center text-white/70 text-sm px-8 text-center">
                {t("carousel.empty", lang)}
              </div>
            ) : (
              slides.map((slide) => (
                <div
                  key={slide.key}
                  className="relative w-full h-[100dvh] flex items-center justify-center"
                  style={{ scrollSnapAlign: "start" }}
                >
                  {/* Portrait column — on desktop the slide is constrained
                      to a 9:16 aspect ratio centered on screen (mimics
                      TikTok/Reels web viewer). On mobile this already
                      matches the viewport width. */}
                  <div
                    className="relative h-full mx-auto overflow-hidden"
                    style={{ width: "min(100%, calc(100dvh * 9 / 16))" }}
                  >
                    <img
                      src={slide.url}
                      alt={slide.loc.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                    />
                    {/* Bottom gradient for text legibility */}
                    <div
                      className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 40%, transparent 100%)",
                      }}
                    />

                    {/* Pin badge top-left */}
                    <div
                      className="absolute top-4 left-4 flex items-center gap-2"
                      style={{
                        top: "max(env(safe-area-inset-top, 0px), 16px)",
                      }}
                    >
                      <span
                        className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold shadow-lg"
                        style={{ backgroundColor: "#B76E6E" }}
                      >
                        {slide.loc.number}
                      </span>
                    </div>

                    {/* Bottom info + CTA */}
                    <div
                      className="absolute inset-x-0 bottom-0 p-6 flex flex-col gap-4"
                      style={{
                        paddingBottom:
                          "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                      }}
                    >
                      <div>
                        <h3 className="text-white text-xl font-semibold font-display drop-shadow">
                          {slide.loc.name}
                        </h3>
                        {slide.loc.nameJa && (
                          <p className="text-white/80 text-sm mt-0.5 drop-shadow">
                            {slide.loc.nameJa}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          onPickLocation(slide.loc);
                          onClose();
                        }}
                        className="self-start px-4 py-2.5 rounded-full bg-white text-ink-0 text-sm font-semibold shadow-lg active:scale-[0.97] transition-transform"
                      >
                        {t("inspiration.cta", lang)}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Scroll hint — only on first slide, fades out */}
          <motion.div
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 2.5, duration: 0.6 }}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white/80 text-xs pointer-events-none flex flex-col items-center gap-1"
          >
            <span className="animate-bounce text-lg">↑</span>
            <span>{t("inspiration.swipeHint", lang)}</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
